"use client";

import Image from "next/image";
import Link from "next/link";
import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import {
  ArrowRight,
  CircleDollarSign,
  Shield,
  Users,
  Smartphone,
  Clock,
  Globe,
  MessageSquare,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Header, MobileBottomNav, Footer } from "@/components/layout";
import { AuthModal } from "@/components/auth";
import { PricingCards } from "@/components/pricing/pricing-cards";
import { HostBanner } from "@/components/common";
import { cn } from "@/lib/utils";
import { createClient } from "@/lib/supabase/client";
import type { User as SupabaseUser } from "@supabase/supabase-js";

// Neden konakla verileri - 4 kart
const WHY_KONAKLA = [
  {
    icon: CircleDollarSign,
    title: "Komisyonsuz Model",
    description: "Aracılar ve komisyon kesintisi yok. Kazancın hepsi sizin.",
    highlighted: true,
  },
  {
    icon: Users,
    title: "Direkt İletişim",
    description: "Misafirlerinizle aracısız, doğrudan iletişim kurun.",
    highlighted: false,
  },
  {
    icon: Smartphone,
    title: "Kolay Yönetim",
    description: "Basit ve kullanıcı dostu panel ile her yerden yönetin.",
    highlighted: false,
  },
  {
    icon: Shield,
    title: "Güvenilir Platform",
    description: "Sadece belgeli ve onaylı evler.",
    highlighted: false,
  },
];

// Nasıl ilan verilir adımları - 3 adım (ev sahibinin kendi ikon görselleriyle)
const HOW_IT_WORKS = [
  {
    image: "/yukle.png",
    title: "İlanınızı Oluşturun",
    description: "Diğer platformlardan aktarın veya manuel olarak ekleyin.",
  },
  {
    image: "/belge.png",
    title: "Belgenizi Yükleyin",
    description: "Turizm Amaçlı Konaklama İzin Belgenizi yükleyin. Aynı gün onay.",
  },
  {
    image: "/ilan.png",
    title: "Yayına Alın",
    description: "İlanınız aktif! Misafirler sizi bulmaya başlasın.",
  },
];

// Not: Abonelik plan verisi lib/constants.ts → SUBSCRIPTION_PLANS içinde tanımlıdır.
// Pricing kartları paylaşımlı `PricingCards` bileşeninden gelir.

export default function ForHostsPage() {
  const router = useRouter();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [user, setUser] = useState<SupabaseUser | null>(null);

  // Komisyon hesaplayıcı state'leri
  const [monthlyIncome, setMonthlyIncome] = useState(50000);
  const [commissionRate, setCommissionRate] = useState(15);

  useEffect(() => {
    const supabase = createClient();
    if (!supabase) return;

    // Mevcut kullanıcıyı al
    const getUser = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      setUser(user);
    };

    getUser();

    // Auth state değişikliklerini dinle
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // İlan oluşturma butonu handler'ı
  const handleCreateListing = () => {
    if (user) {
      // Kullanıcı giriş yapmışsa direkt yönlendir
      router.push("/listings/new");
    } else {
      // Giriş yapmamışsa AuthModal aç
      setAuthModalOpen(true);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Header />

      <main className="flex-1 pb-20 md:pb-0">
        {/* Hero Section - Görsel yukarıda */}
        <section className="bg-white">
          <div className="mx-auto max-w-7xl px-4 pt-6 sm:px-6 md:pt-10 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <h1 className="text-foreground text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
                <span className="text-foreground">Sizi</span>{" "}
                <span className="text-primary">Anlıyoruz!</span>
              </h1>

              <p className="text-muted-foreground mx-auto mt-4 max-w-2xl text-lg md:mt-6 md:text-xl">
                konakla üzerinden doğrudan, komisyonsuz ve kendi kurallarınız ile misafirlerinize
                ulaşın! <span className="text-foreground font-semibold">Sizde katılın.</span>
              </p>

              <div className="mt-6 md:mt-8">
                <Button
                  size="lg"
                  className="bg-foreground hover:bg-foreground/90 gap-2 rounded-full px-8 text-white"
                  onClick={handleCreateListing}
                >
                  Evinizi Listeleyin
                  <ArrowRight className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>

          {/* Hero Görseli - Daha yukarıda */}
          <div className="mt-8 md:mt-12">
            <Image
              src="/evsahibi_hero.jpg"
              alt="konakla ev sahipleri"
              width={1920}
              height={600}
              className="w-full object-cover"
              priority
            />
          </div>
        </section>

        {/* Neden konakla - Kart Tasarımı */}
        <section className="bg-slate-50 py-16 md:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
              {/* Sol Taraf - İçerik */}
              <div>
                <h2 className="mt-4 text-3xl font-bold tracking-tight md:text-4xl">
                  Neden <span className="text-primary">konakla</span>?
                </h2>
                <p className="text-muted-foreground mt-4 text-lg leading-relaxed">
                  Sizin ihtiyaçlarınıza odaklanan ekibimiz, derin sektör bilgisi ve yenilikçi
                  stratejilerle kalıcı büyüme sağlamak için çözümler sunuyor.
                </p>
                <div className="mt-8">
                  <Button
                    size="lg"
                    className="bg-primary hover:bg-primary/90 gap-2 rounded-full px-8 text-white"
                    onClick={handleCreateListing}
                  >
                    Hemen Başla
                    <ArrowRight className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              {/* Sağ Taraf - 4 Kart Grid */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {WHY_KONAKLA.map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <div
                      key={index}
                      className={cn(
                        "rounded-2xl p-6 transition-all duration-300",
                        feature.highlighted
                          ? "bg-primary text-white shadow-xl"
                          : "bg-white shadow-sm hover:shadow-lg"
                      )}
                    >
                      <div
                        className={cn(
                          "inline-flex h-12 w-12 items-center justify-center rounded-xl",
                          feature.highlighted ? "bg-white/20" : "bg-primary/10"
                        )}
                      >
                        <Icon
                          className={cn(
                            "h-6 w-6",
                            feature.highlighted ? "text-white" : "text-primary"
                          )}
                        />
                      </div>
                      <h3
                        className={cn(
                          "mt-4 text-lg font-semibold",
                          feature.highlighted ? "text-white" : "text-foreground"
                        )}
                      >
                        {feature.title}
                      </h3>
                      <p
                        className={cn(
                          "mt-2 text-sm leading-relaxed",
                          feature.highlighted ? "text-white/80" : "text-muted-foreground"
                        )}
                      >
                        {feature.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Komisyon Hesaplayıcı */}
        <section className="py-16 md:py-24">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold tracking-tight md:text-4xl">
                Ne Kadar <span className="text-primary">Komisyon</span> Veriyorsun?
              </h2>
              <p className="text-muted-foreground mx-auto mt-3 max-w-xl">
                Bilgileri gir, farkı gör.
              </p>
            </div>

            <div className="border-border/60 mt-10 rounded-3xl border bg-white p-6 shadow-sm md:p-10">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {/* Aylık Gelir */}
                <div>
                  <label className="text-muted-foreground block text-sm font-medium">
                    Aylık Geliriniz
                  </label>
                  <div className="relative mt-2">
                    <Input
                      type="number"
                      value={monthlyIncome}
                      onChange={(e) => setMonthlyIncome(Math.max(0, Number(e.target.value)))}
                      className="h-12 pr-10 text-lg font-bold"
                      min={0}
                    />
                    <span className="text-muted-foreground absolute top-1/2 right-3 -translate-y-1/2 text-sm font-medium">
                      TL
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {[25000, 50000, 100000, 200000].map((val) => (
                      <button
                        key={val}
                        onClick={() => setMonthlyIncome(val)}
                        className={cn(
                          "rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors",
                          monthlyIncome === val
                            ? "border-primary bg-primary text-white"
                            : "border-border hover:bg-muted"
                        )}
                      >
                        {(val / 1000).toLocaleString("tr-TR")}K
                      </button>
                    ))}
                  </div>
                </div>

                {/* Komisyon Oranı */}
                <div>
                  <label className="text-muted-foreground block text-sm font-medium">
                    Ödediğiniz Komisyon Oranı
                  </label>
                  <div className="relative mt-2">
                    <Input
                      type="number"
                      value={commissionRate}
                      onChange={(e) =>
                        setCommissionRate(Math.min(50, Math.max(0, Number(e.target.value))))
                      }
                      className="h-12 pr-10 text-lg font-bold"
                      min={0}
                      max={50}
                    />
                    <span className="text-muted-foreground absolute top-1/2 right-3 -translate-y-1/2 text-sm font-medium">
                      %
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {[10, 15, 18, 20].map((val) => (
                      <button
                        key={val}
                        onClick={() => setCommissionRate(val)}
                        className={cn(
                          "rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors",
                          commissionRate === val
                            ? "border-primary bg-primary text-white"
                            : "border-border hover:bg-muted"
                        )}
                      >
                        %{val}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Sonuçlar */}
              {(() => {
                const yearlyIncome = monthlyIncome * 12;
                const rate = commissionRate / 100;
                const yearlyLoss = yearlyIncome * rate;

                const formatTL = (n: number) =>
                  new Intl.NumberFormat("tr-TR", {
                    style: "currency",
                    currency: "TRY",
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }).format(n);

                return (
                  <div className="mt-8">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="rounded-2xl border border-red-200 bg-red-50/30 p-5">
                        <p className="text-sm font-semibold text-red-600/80">Diğer Platformlar</p>
                        <p className="text-muted-foreground mt-0.5 text-xs">
                          %{commissionRate} komisyon ile
                        </p>
                        <div className="mt-4 space-y-2.5">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Yıllık gelir</span>
                            <span className="font-semibold">{formatTL(yearlyIncome)}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-red-600">Komisyon</span>
                            <span className="font-bold text-red-600">-{formatTL(yearlyLoss)}</span>
                          </div>
                          <div className="border-t border-red-200 pt-2.5">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium">Elinize geçen</span>
                              <span className="text-lg font-bold">
                                {formatTL(yearlyIncome - yearlyLoss)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="border-primary bg-primary/5 rounded-2xl border-2 p-5">
                        <p className="text-primary text-sm font-semibold">konakla</p>
                        <p className="text-muted-foreground mt-0.5 text-xs">%0 komisyon</p>
                        <div className="mt-4 space-y-2.5">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Yıllık gelir</span>
                            <span className="font-semibold">{formatTL(yearlyIncome)}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-green-600">Komisyon</span>
                            <span className="font-bold text-green-600">{formatTL(0)}</span>
                          </div>
                          <div className="border-primary/20 border-t pt-2.5">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium">Elinize geçen</span>
                              <span className="text-lg font-bold text-green-600">
                                {formatTL(yearlyIncome)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Tasarruf */}
                    <div className="mt-4 rounded-2xl border border-green-200 bg-green-50 p-4 text-center">
                      <p className="text-sm font-medium text-green-800">
                        konakla&apos;da yıllık tasarrufunuz
                      </p>
                      <p className="mt-1 text-3xl font-bold text-green-700 md:text-4xl">
                        {formatTL(yearlyLoss)}
                      </p>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </section>

        {/* Nasıl Çalışır - 3 Adım */}
        <section className="bg-slate-50 py-16 md:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold tracking-tight md:text-4xl">Nasıl İlan Verilir?</h2>
              <p className="text-muted-foreground mx-auto mt-3 max-w-xl">
                3 basit adımda konaklatın.
              </p>
            </div>

            {/* Adımlar — anasayfa "Nasıl Kullanılır?" deseni: görsel + numara +
                başlık + açıklama. Görseller ev sahibinin kendi ikonları. */}
            <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-3 md:gap-6">
              {HOW_IT_WORKS.map((step, index) => (
                <div key={index} className="group text-center">
                  {/* İkon doğrudan — çerçeve/kart/zemin yok, sadece görsel;
                      ikondan hafifçe büyük. */}
                  <Image
                    src={step.image}
                    alt={step.title}
                    width={224}
                    height={224}
                    className="mx-auto mb-4 h-24 w-24 object-contain transition-transform duration-300 group-hover:scale-105 md:mb-6 md:h-28 md:w-28"
                  />
                  <div className="bg-primary inline-flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold text-white md:h-10 md:w-10 md:text-base">
                    {index + 1}
                  </div>
                  <h3 className="text-foreground mt-3 text-lg font-semibold md:text-xl">
                    {step.title}
                  </h3>
                  <p className="text-muted-foreground mt-1.5 text-sm md:text-base">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-12 text-center">
              <Button size="lg" className="gap-2 rounded-full px-8" onClick={handleCreateListing}>
                Hemen Başla
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </section>

        {/* Abonelik Paketleri — beyaz zemin (üstteki slate "Nasıl İlan Verilir?"
            bölümüyle karışmasın diye alternasyon) */}
        <section className="py-16 md:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold tracking-tight md:text-4xl">Abonelik Paketleri</h2>
              <p className="text-muted-foreground mx-auto mt-3 max-w-xl">
                İlk ilanınız her zaman ücretsiz. Yıllık ödemelerde %20 indirim.
              </p>
            </div>

            <div className="mt-10">
              <PricingCards mode="marketing" onMarketingCta={handleCreateListing} />
            </div>
          </div>
        </section>

        {/* konakla host tanıtım banner'ı */}
        <HostBanner />

        {/* Final CTA */}
        <section className="bg-primary py-16 md:py-24">
          <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white md:text-4xl">Hemen Başlayın!</h2>
            {/* ⚠️ BURAYA SAYI YAZMA. Eskiden "Yüzlerce ev sahibi gibi..." yazıyordu;
                gerçek sayı iki basamaklıydı. Doğrulanamayan sayısal büyüklük iddiası
                TKHK m.61 kapsamında aldatıcı reklamdır ve platformun kendi kural
                setinde de yasaktır (CLAUDE.md §2). Sayı gerçekten anlatılmak
                isteniyorsa canlı veriden okunup gösterilmeli, elle yazılmamalı. */}
            <p className="mx-auto mt-4 max-w-xl text-lg text-white/70">
              Evini komisyonsuz listeleyen ev sahipleri arasına siz de katılın.
            </p>

            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Button
                size="lg"
                className="text-foreground gap-2 rounded-full bg-white px-8 hover:bg-white/90"
                onClick={handleCreateListing}
              >
                Ücretsiz İlan Ver
                <ArrowRight className="h-5 w-5" />
              </Button>
              <Link href="/about#iletisim">
                <Button
                  size="lg"
                  variant="ghost"
                  className="hover:text-primary gap-2 rounded-full border border-white/40 text-white hover:bg-white"
                >
                  <MessageSquare className="h-5 w-5" />
                  Sorularınız mı var?
                </Button>
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap items-center justify-center gap-8 text-sm text-white/60">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                <span>Güvenli platform</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                <span>İstediğiniz zaman iptal</span>
              </div>
              <div className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                <span>Tüm Türkiye</span>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <MobileBottomNav />

      {/* Auth Modal - Giriş yapmamış kullanıcılar için */}
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        redirectTo="/listings/new"
      />
    </div>
  );
}
